import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';
import {UsersService} from '../../users/users.service';
import {Observable, Subject} from 'rxjs';
import {AuthenticationService} from '../authentication.service';
import {MustMatch} from 'src/app/shared/validators/must-match.validator';
import {marker} from '@biesbjerg/ngx-translate-extract-marker';
import {NotificationService} from 'src/app/shared/notification.service';
import moment from 'moment';
import {TranslateService} from '@ngx-translate/core';
import {IPolicy} from '../password-policy';

@Component({
    selector: 'app-change-password-common',
    templateUrl: './change-password-common.component.html',
    styleUrls: ['./change-password-common.component.scss'],
    standalone: false
})
export class ChangePasswordCommonComponent implements OnInit, OnDestroy {
  cpForm: FormGroup;
  private destroyed$: Subject<void> = new Subject<void>();
  submitted = false;
  loading = false;
  errorMessage: Observable<string>;
  successMessage: Observable<string>;
  numberOfPasswordChangesAllowedPerDay = 3;
  numberOfPasswordRemembered = 3;
  policy: IPolicy;
  pageTitle: string = marker('Create a new password');

  passwordLengthText = '';
  passwordLengthErrorText = '';
  numberOfPasswordRememberedText = '';
  numberOfRulesToApplyText = '';

  passwordTextsSubscription = this.translateService
    .stream([
      marker('Minimum of 111 characters in length.'),
      marker('must be at least 111 characters long.'),
      marker('Cannot be the same password as the previous 111 passwords.'),
      marker('Must contain characters from at least 111 of these groupings')]
    ).subscribe(res => {
      this.passwordLengthText = res['Minimum of 111 characters in length.'];
      this.passwordLengthErrorText =  res['must be at least 111 characters long.'];
      this.numberOfPasswordRememberedText =  res['Cannot be the same password as the previous 111 passwords.'];
      this.numberOfRulesToApplyText =  res['Must contain characters from at least 111 of these groupings'];

      this.updatePolicyDependentTexts();
    });

  constructor(
    private formBuilder: FormBuilder,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private usersService: UsersService,
  ) {
  }

  ngOnInit() {
    // @ts-ignore
    this.cpForm = this.formBuilder.group({
      currentPassword: ['', [Validators.required]],
      password: ['', [Validators.required]],
      password2: ['', [Validators.required]],
    },
    {
        validator: MustMatch('password', 'password2'),
    });

    this.getPolicy();
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    const passwordExpiresOn = moment(currentUser.passwordExpiresOn, 'YYYY-MM-DD').toDate();
    const today = new Date();
    if (passwordExpiresOn < today) {
      this.pageTitle = marker('Password Expired');
    }

  }

  getPolicy() {
    this.usersService.getPolicy()
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(
        (policy) => {
          this.policy = policy;
          this.cpForm.get('password').setValidators([Validators.required, Validators.minLength(this.policy.passwordLength)]);
          this.cpForm.get('password').updateValueAndValidity();
          this.cpForm.get('password2').setValidators([Validators.required, Validators.minLength(this.policy.passwordLength)]);
          this.cpForm.get('password2').updateValueAndValidity();

          this.updatePolicyDependentTexts();
        },
      );
  }

  private updatePolicyDependentTexts() {
    if (this.policy) {
      this.passwordLengthText = this.passwordLengthText.replace('111', this.policy?.passwordLength.toString());
      this.passwordLengthErrorText = this.passwordLengthErrorText.replace('111', this.policy?.passwordLength.toString());

      this.numberOfPasswordRememberedText =
        this.numberOfPasswordRememberedText.replace('111', this.policy?.numberOfPasswordRemembered.toString());
      this.numberOfRulesToApplyText = this.numberOfRulesToApplyText.replace('111', this.policy?.numberOfRulesToApply.toString());
    }
  }

  send() {
    this.submitted = true;
    this.loading = true;

    this.usersService.changePassword(this.cpForm.get('currentPassword').value, this.cpForm.get('password').value)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(
        (response) => {
          this.loading = false;

          const status = response?.status;
          this.numberOfPasswordChangesAllowedPerDay = this.policy?.numberOfPasswordChangesAllowedPerDay;
          this.numberOfPasswordRemembered = this.policy?.numberOfPasswordRemembered;
          if (status === this.usersService.PASSWORD_CHANGE_REQUEST_STATUS_SUCCESS) {
            this.successMessage = this.translateService.stream(marker('Password changed successfully.'));
            this.notificationService.success(this.successMessage);
            setTimeout(() => {
              this.authenticationService.logout();
            }, 1000);
          } else if (status === this.usersService.PASSWORD_CHANGE_REQUEST_STATUS_USERNAME_PASSWORD_MISMATCH) {
            this.errorMessage = this.translateService.stream(marker('Current password is incorrect. Please try again.'));
            this.notificationService.error(this.errorMessage);
          } else if (status === this.usersService.PASSWORD_CHANGE_REQUEST_STATUS_NEW_AND_RECENT_PASSWORDS_SAME) {
            this.translateService.get([marker('New Password should not match the previous'), marker('passwords')]).subscribe(translation => {
              this.notificationService.error(translation['New Password should not match the previous'] + ' ' + this.numberOfPasswordRemembered + ' ' + translation['passwords'] + ".");
            })
          } else if (status === this.usersService.PASSWORD_CHANGE_REQUEST_STATUS_TOO_MANY_PASSWORD_CHANGES) {
            this.translateService.get([marker('Oops! The password cannot be changed more than'), marker('times per day')]).subscribe(translation => {
              this.notificationService.error(translation['Oops! The password cannot be changed more than'] + ' ' + this.numberOfPasswordRemembered + ' ' + translation['times per day']);
            })
          } else if (status === this.usersService.PASSWORD_FAILS_PASSWORD_POLICY) {
            this.errorMessage = this.translateService.stream(marker('Oops! The password fails password policy.'));
            this.notificationService.error(this.errorMessage);
          }
        },
        (error: any) => {
          this.loading = false;
        },
        () =>  { this.loading = false; },
      );

  }

  sendEnabled(): boolean {
    return this.cpForm.valid && this.cpForm.touched;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}

